import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ServerApiClient } from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'
import { FgHelp } from '../help/FgHelp'
import {
  REQUIRED_SCOPE,
  buildApproveBody,
  buildDenyBody,
  canApprove,
  clientWarning,
  consentErrorMessage,
  deniedRequestedScopes,
  filterFirmGroups,
  firmSelectionState,
  groupCompaniesByFirm,
  initialScopes,
  offeredScopes,
  scopeLabel,
  setFirmSelection,
  summarizeCapabilities,
  toggleScope,
  type CompanyMode,
  type ConsentDecisionBody,
  type OAuthConsentDetails,
} from './oauthConsentModel'

const colors = {
  ink: '#132b3a',
  muted: '#5f7480',
  border: '#d9e4e8',
  brand: '#174f6d',
  accent: '#19765b',
  panel: '#f3f7f8',
}

const card: CSSProperties = { padding: 16, borderRadius: 14, border: `1px solid ${colors.border}`, background: '#fff' }

async function sessionToken(): Promise<string | null> {
  const session = await ensureApiSessionReady()
  return session.token || null
}

function authorizationUrl(requestId: string): string {
  const api = new ServerApiClient()
  return `${api.baseUrl}/api/platform/oauth/authorization/${encodeURIComponent(requestId)}`
}

export function OAuthConsentPage({ requestId }: { requestId: string }) {
  const [details, setDetails] = useState<OAuthConsentDetails | null>(null)
  const [scopes, setScopes] = useState<string[]>([REQUIRED_SCOPE])
  const [mode, setMode] = useState<CompanyMode>('all_accessible')
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const token = await sessionToken()
        if (!token) {
          const next = `${window.location.pathname}${window.location.search}`
          window.location.href = `/login?next=${encodeURIComponent(next)}`
          return
        }
        const response = await fetch(authorizationUrl(requestId), { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' })
        const body = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(consentErrorMessage(body?.error, 'This connection request is no longer available.'))
        if (cancelled) return
        const next = body as OAuthConsentDetails
        setDetails(next)
        setScopes(initialScopes(next))
      } catch (reason: any) {
        if (!cancelled) setError(reason?.message || String(reason))
      }
    }
    void load()
    return () => { cancelled = true }
  }, [requestId])

  const groups = useMemo(() => groupCompaniesByFirm(details?.firms ?? []), [details])
  const visibleGroups = useMemo(() => filterFirmGroups(groups, query), [groups, query])
  const capabilities = useMemo(() => summarizeCapabilities(scopes), [scopes])

  const submit = async (body: ConsentDecisionBody) => {
    setBusy(true)
    setError(null)
    try {
      const token = await sessionToken()
      if (!token) throw new Error('Your FuseGrid session has expired. Sign in and try again.')
      const response = await fetch(authorizationUrl(requestId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        cache: 'no-store',
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result?.redirect_to) throw new Error(consentErrorMessage(result?.error, 'FuseGrid could not complete the connection.'))
      window.location.href = String(result.redirect_to)
    } catch (reason: any) {
      setBusy(false)
      setError(reason?.message || String(reason))
    }
  }

  const companyCount = details?.company_count ?? 0
  const approvable = Boolean(details) && canApprove(mode, selected, companyCount)
  const warning = details ? clientWarning(details.client) : null
  const denied = details ? deniedRequestedScopes(details) : []

  return (
    <main style={{ width: '100%', height: '100dvh', overflowY: 'auto', boxSizing: 'border-box', display: 'grid', placeItems: 'start center', padding: 24, background: 'radial-gradient(circle at top left, #dbeafe 0, #f8fafc 42%, #ecfdf5 100%)', color: colors.ink, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif', scrollbarGutter: 'stable' }}>
      <section style={{ width: 'min(720px, 100%)', background: 'rgba(255,255,255,.96)', border: `1px solid ${colors.border}`, borderRadius: 24, padding: '32px clamp(22px, 5vw, 44px)', boxShadow: '0 24px 70px rgba(24,65,86,.16)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
          {details?.client.logo_uri ? (
            <img src={details.client.logo_uri} alt="" width={48} height={48} style={{ borderRadius: 14, objectFit: 'contain', border: `1px solid ${colors.border}` }} />
          ) : (
            <div aria-hidden style={{ width: 48, height: 48, borderRadius: 14, display: 'grid', placeItems: 'center', background: colors.brand, color: 'white', fontFamily: 'Georgia, serif', fontWeight: 800, fontSize: 21 }}>FG</div>
          )}
          <div>
            <h1 style={{ fontWeight: 800, fontSize: 20, margin: 0 }}>{details ? `Connect ${details.client_name} to FuseGrid` : 'Connect an AI app to FuseGrid'}</h1>
            {details ? <div style={{ color: colors.muted, marginTop: 3 }}>Signed in as {details.user_email}</div> : null}
          </div>
        </div>

        {warning ? (
          <div role="alert" data-testid="client-warning" style={{ padding: 14, borderRadius: 12, background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', fontWeight: 700, marginBottom: 18 }}>
            {warning}
            <div style={{ fontWeight: 400, marginTop: 4, fontSize: 14 }}>FuseGrid cannot verify who runs this app. Only continue if you started this connection yourself.</div>
          </div>
        ) : null}

        {error ? <div role="alert" data-testid="consent-error" style={{ padding: 14, borderRadius: 12, background: '#fff1f2', color: '#9f1239', marginBottom: 18 }}>{error}</div> : null}
        {!details && !error ? <p style={{ color: colors.muted }}>Loading the connection request…</p> : null}

        {details ? (
          <>
            <p style={{ fontSize: 17, lineHeight: 1.55, margin: '0 0 18px' }}>
              <strong>{details.client_name}</strong> wants to use FuseGrid on your behalf.
            </p>
            <FgHelp id="consent.what_connecting_does" variant="text" label="What connecting does" />

            {companyCount <= 0 ? (
              <div role="status" data-testid="no-companies" style={{ ...card, marginTop: 18, background: '#fff7ed', color: '#9a3412' }}>
                You don't have access to any companies yet. Ask your firm to share a company with you, then start the connection again.
              </div>
            ) : (
              <fieldset style={{ border: 0, padding: 0, margin: '20px 0 0' }}>
                <legend style={{ fontWeight: 750, marginBottom: 10 }}>Which companies can it use?</legend>
                <div style={{ display: 'grid', gap: 10 }}>
                  <label style={{ ...card, cursor: 'pointer', borderColor: mode === 'all_accessible' ? colors.accent : colors.border }}>
                    <input type="radio" name="company_mode" value="all_accessible" checked={mode === 'all_accessible'} onChange={() => setMode('all_accessible')} style={{ marginRight: 10 }} />
                    All companies I can access ({companyCount}), including ones added later
                  </label>
                  <label style={{ ...card, cursor: 'pointer', borderColor: mode === 'selected' ? colors.accent : colors.border }}>
                    <input type="radio" name="company_mode" value="selected" checked={mode === 'selected'} onChange={() => setMode('selected')} style={{ marginRight: 10 }} />
                    Only selected companies{mode === 'selected' ? ` (${selected.size} selected)` : ''}
                  </label>
                </div>
                <FgHelp id="consent.all_companies" variant="text" label="About all companies" />
                {mode === 'selected' ? (
                  <div data-testid="company-picker" style={{ ...card, marginTop: 12 }}>
                    <input
                      type="search"
                      aria-label="Search companies"
                      placeholder="Search companies or firms"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: `1px solid ${colors.border}`, marginBottom: 12, font: 'inherit' }}
                    />
                    <div style={{ maxHeight: 320, overflowY: 'auto', display: 'grid', gap: 14 }}>
                      {visibleGroups.length === 0 ? <p style={{ color: colors.muted, margin: 0 }}>No companies match “{query}”.</p> : null}
                      {visibleGroups.map((group) => {
                        const state = firmSelectionState(group, selected)
                        return (
                          <div key={group.firm_site_id} data-firm={group.firm_site_id}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 750 }}>
                              <input
                                type="checkbox"
                                aria-label={`Select all companies in ${group.firm_name}`}
                                checked={state === 'all'}
                                ref={(el) => { if (el) el.indeterminate = state === 'some' }}
                                onChange={(event) => setSelected((prev) => setFirmSelection(prev, group, event.target.checked))}
                              />
                              {group.firm_name}
                            </label>
                            <div style={{ display: 'grid', gap: 6, margin: '8px 0 0 26px' }}>
                              {group.companies.map((company) => (
                                <label key={company.workbook_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input
                                    type="checkbox"
                                    value={company.workbook_id}
                                    checked={selected.has(company.workbook_id)}
                                    onChange={(event) => {
                                      const checked = event.target.checked
                                      setSelected((prev) => {
                                        const next = new Set(prev)
                                        if (checked) next.add(company.workbook_id)
                                        else next.delete(company.workbook_id)
                                        return next
                                      })
                                    }}
                                  />
                                  <span>{company.display_name}</span>
                                  <span style={{ color: colors.muted, fontSize: 13 }}>{company.company_key}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : null}
              </fieldset>
            )}

            <fieldset style={{ border: 0, padding: 0, margin: '22px 0 0' }}>
              <legend style={{ fontWeight: 750, marginBottom: 10 }}>What can it do?</legend>
              <div style={{ display: 'grid', gap: 8 }}>
                {offeredScopes(details).map((scope) => (
                  <label key={scope} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      value={scope}
                      checked={scopes.includes(scope)}
                      disabled={scope === REQUIRED_SCOPE}
                      onChange={(event) => setScopes((prev) => toggleScope(prev, scope, event.target.checked))}
                    />
                    <span>{scopeLabel(scope)}{scope === REQUIRED_SCOPE ? <span style={{ color: colors.muted, fontSize: 13 }}> (always included)</span> : null}</span>
                  </label>
                ))}
              </div>
              {denied.map((scope) => (
                <p key={scope} data-testid="denied-scope" style={{ color: colors.muted, fontSize: 14, margin: '8px 0 0' }}>
                  {details.client_name} asked for “{scopeLabel(scope)}”; your role does not permit it.
                </p>
              ))}
              {details.shares_email ? (
                <p data-testid="email-disclosure" style={{ color: colors.ink, fontSize: 14, margin: '10px 0 0' }}>
                  {details.client_name} will also receive your email address ({details.user_email}) to confirm who you are. It gives no access to financial data.
                </p>
              ) : null}
              <FgHelp id="consent.scopes" variant="text" label="About permissions" />
              {details.client.registration_kind === 'loopback_dcr' ? <FgHelp id="consent.local_apps" variant="text" label="About local apps" /> : null}
            </fieldset>

            {capabilities.length ? (
              <div data-testid="capability-summary" style={{ padding: 16, borderRadius: 14, background: colors.panel, marginTop: 20 }}>
                <div style={{ fontWeight: 750, marginBottom: 8 }}>With these permissions it can</div>
                {capabilities.map((summary) => (
                  <div key={summary.scope} style={{ marginBottom: 8 }}>
                    <div style={{ color: colors.ink, fontSize: 14, fontWeight: 650 }}>{summary.label} · {summary.count} {summary.count === 1 ? 'capability' : 'capabilities'}</div>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20, color: '#405d6b', lineHeight: 1.6 }}>
                      {summary.titles.map((title) => <li key={title}>{title}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
              <button type="button" data-action="deny" disabled={busy} onClick={() => void submit(buildDenyBody())} style={{ padding: '12px 18px', borderRadius: 12, border: '1px solid #b9c8cf', background: '#fff', color: '#294b5c', fontWeight: 750, cursor: busy ? 'not-allowed' : 'pointer' }}>Deny</button>
              <button
                type="button"
                data-action="approve"
                disabled={busy || !approvable}
                onClick={() => void submit(buildApproveBody(scopes, mode, selected))}
                style={{ padding: '12px 20px', borderRadius: 12, border: 0, background: busy || !approvable ? '#9fb4bd' : colors.brand, color: '#fff', fontWeight: 800, cursor: busy || !approvable ? 'not-allowed' : 'pointer' }}
              >
                {busy ? 'Connecting…' : 'Approve'}
              </button>
            </div>
            <FgHelp id="consent.revoke" variant="text" label="Disconnecting later" />
          </>
        ) : null}
      </section>
    </main>
  )
}
